import {
  Badge,
  Box,
  Collapse,
  Divider,
  Flex,
  HStack,
  Icon,
  Text,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { MdChevronRight, MdExpandMore } from "react-icons/md";

/**
 * Sticky "build progress" card that summarises the user's choices so
 * far. Renders a one-line headline by default; click to expand into a
 * fuller breakdown. Designed to live above each step's content from
 * Step 1 onwards.
 */

const MECHANISM_LABEL = {
  tool: "tool",
  contact_sync: "save contact",
  conversation_sync: "save transcript",
  summary_email: "email summary",
  webhook: "webhook",
};

const TOOL_LABELS = {
  hubspot: "HubSpot",
  zoho: "Zoho",
  gohighlevel: "GoHighLevel",
  google_calendar: "Google Calendar",
  twilio_sms: "Twilio SMS",
  paypal: "PayPal",
  freshdesk: "Freshdesk",
  postgres_lookup: "Postgres",
  modmed: "ModMed",
  call_transfer: "Call transfer",
  call_disconnect: "Call hangup",
};

function resolveToolKey(raw, crmChoice) {
  return raw === "crm_picker" ? crmChoice || "CRM (pick later)" : raw;
}

function fmtFieldValue(field, value) {
  if (value === "" && field.can_skip)
    return field.skip_fallback ? `(${field.skip_fallback})` : "(skipped)";
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (value.length <= 3) return value.join(", ");
    return `${value.slice(0, 3).join(", ")} +${value.length - 3} more`;
  }
  return String(value);
}

export default function WizardSummary({ draft }) {
  const disclosure = useDisclosure({ defaultIsOpen: false });
  const { config, channel, fieldValues, outcomeSelections, crmChoice, knowledgeFiles } = draft;
  if (!config && !channel) return null;

  const channelLabel = channel === "voice" ? "Voice" : channel === "chat" ? "Chat" : null;
  const useCase = config?.goal?.primary;
  const industry = config?.industry_tag;

  // Filled fields (anything user touched, including skipped)
  const filledFields = (config?.fields || [])
    .map((f) => ({ field: f, display: fmtFieldValue(f, fieldValues?.[f.key]) }))
    .filter((x) => x.display !== null);

  // Active outcomes
  const activeOutcomes = (config?.recommended_outcomes || []).filter(
    (o) => outcomeSelections?.[o.key]
  );

  // Tool keys union (in-conversation tools + outcome-driven tools)
  const toolKeys = new Set();
  activeOutcomes.forEach((o) => {
    if (o.tool_key) toolKeys.add(resolveToolKey(o.tool_key, crmChoice));
  });
  (config?.recommended_tools || []).forEach((t) =>
    toolKeys.add(resolveToolKey(t.tool_key, crmChoice))
  );
  const toolList = [...toolKeys].filter((k) => k);

  const knowledgeCount = (knowledgeFiles || []).length;

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="blue.200"
      borderRadius="md"
      shadow="sm"
      overflow="hidden"
    >
      <Flex
        align="center"
        px={4}
        py={2}
        cursor="pointer"
        onClick={disclosure.onToggle}
        _hover={{ bg: "blue.50" }}
        gap={3}
      >
        <Badge colorScheme="blue" variant="solid" fontSize="0.65rem">
          Building
        </Badge>
        <Text fontSize="sm" color="gray.700" flex="1" noOfLines={1}>
          {[
            channelLabel,
            useCase,
            industry,
            activeOutcomes.length
              ? `${activeOutcomes.length} outcome${activeOutcomes.length > 1 ? "s" : ""}`
              : null,
            toolList.length
              ? `${toolList.length} tool${toolList.length > 1 ? "s" : ""}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ") || "Nothing yet"}
        </Text>
        <Icon
          as={disclosure.isOpen ? MdExpandMore : MdChevronRight}
          color="gray.500"
        />
      </Flex>

      <Collapse in={disclosure.isOpen}>
        <Box px={4} py={3}>
          <VStack spacing={3} align="stretch">
            {(channelLabel || useCase) && (
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                  AGENT
                </Text>
                <HStack spacing={2} flexWrap="wrap">
                  {channelLabel && (
                    <Badge colorScheme={channel === "voice" ? "purple" : "blue"}>
                      {channelLabel}
                    </Badge>
                  )}
                  {industry && (
                    <Text fontSize="sm" color="gray.700">
                      {industry}
                    </Text>
                  )}
                  {useCase && (
                    <Text fontSize="sm" color="gray.500">
                      · {useCase}
                    </Text>
                  )}
                </HStack>
              </Box>
            )}

            {filledFields.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                    BUSINESS DETAILS
                  </Text>
                  <VStack spacing={1} align="stretch">
                    {filledFields.map(({ field, display }) => (
                      <HStack key={field.key} fontSize="sm">
                        <Text color="gray.500" minW="140px">
                          {field.label}
                        </Text>
                        <Text color="gray.800" noOfLines={1} flex="1">
                          {display}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </>
            )}

            {activeOutcomes.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                    WHEN THE GOAL IS ACHIEVED
                  </Text>
                  <Wrap spacing={1}>
                    {activeOutcomes.map((o) => (
                      <WrapItem key={o.key}>
                        <Badge
                          colorScheme="green"
                          variant="subtle"
                          fontSize="0.7rem"
                          textTransform="none"
                        >
                          {o.label}
                          {MECHANISM_LABEL[o.mechanism] && (
                            <Text as="span" color="gray.500" ml={1}>
                              · {MECHANISM_LABEL[o.mechanism]}
                            </Text>
                          )}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              </>
            )}

            {toolList.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                    INTEGRATIONS
                  </Text>
                  <Wrap spacing={1}>
                    {toolList.map((k) => (
                      <WrapItem key={k}>
                        <Badge colorScheme="blue" variant="subtle" fontSize="0.7rem">
                          {TOOL_LABELS[k] || k}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              </>
            )}

            {knowledgeCount > 0 && (
              <>
                <Divider />
                <HStack fontSize="sm">
                  <Text fontWeight="medium">Knowledge:</Text>
                  <Text color="gray.700">
                    {knowledgeCount} file{knowledgeCount > 1 ? "s" : ""} staged
                  </Text>
                </HStack>
              </>
            )}
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
}
